import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Shield,
  UserCog,
  Key,
  Mail,
  Calendar
} from 'lucide-react';
import { formatToLocalDateTime } from '../../lib/dateUtils';

const TeamManagement = () => {
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [resetPasswordMember, setResetPasswordMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [newMember, setNewMember] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'event_manager',
    assigned_events: []
  });

  const fetchMembers = useCallback(async () => {
    try {
      const response = await api.get('/team/members');
      setMembers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view team members');
      } else {
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchEvents();
  }, [fetchMembers, fetchEvents]);

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      await api.post('/team/members', newMember);
      toast.success('Team member created successfully');
      setCreateDialogOpen(false);
      setNewMember({
        email: '',
        password: '',
        full_name: '',
        role: 'event_manager',
        assigned_events: []
      });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team member');
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/team/members/${editingMember.member_id}`, {
        full_name: editingMember.full_name,
        role: editingMember.role,
        assigned_events: editingMember.assigned_events,
        status: editingMember.status
      });
      toast.success('Team member updated successfully');
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      await api.delete(`/team/members/${memberId}`);
      toast.success('Team member deleted');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team member');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/team/members/${resetPasswordMember.member_id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordMember(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleEventAssignment = (eventId, isNew = false) => {
    if (isNew) {
      const current = newMember.assigned_events;
      if (current.includes(eventId)) {
        setNewMember({ ...newMember, assigned_events: current.filter(id => id !== eventId) });
      } else {
        setNewMember({ ...newMember, assigned_events: [...current, eventId] });
      }
    } else if (editingMember) {
      const current = editingMember.assigned_events || [];
      if (current.includes(eventId)) {
        setEditingMember({ ...editingMember, assigned_events: current.filter(id => id !== eventId) });
      } else {
        setEditingMember({ ...editingMember, assigned_events: [...current, eventId] });
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team Management
          </h1>
          <p className="text-gray-500">Manage team members and their access permissions</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-button">
              <Plus className="h-4 w-4 mr-2" /> Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>Create a new team member account</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateMember} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={newMember.full_name}
                  onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  required
                />
                <p className="text-xs text-gray-500">
                  Must include uppercase, lowercase, number, and special character
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={newMember.role}
                  onValueChange={(v) => setNewMember({ ...newMember, role: v, assigned_events: v === 'super_admin' ? [] : newMember.assigned_events })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        Super Admin
                      </div>
                    </SelectItem>
                    <SelectItem value="event_manager">
                      <div className="flex items-center gap-2">
                        <UserCog className="h-4 w-4 text-blue-600" />
                        Event Manager
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {newMember.role === 'event_manager' && (
                <div className="space-y-2">
                  <Label>Assigned Events</Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {events.length === 0 ? (
                      <p className="text-sm text-gray-500">No events available</p>
                    ) : (
                      events.map((event) => (
                        <div key={event.event_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`new-${event.event_id}`}
                            checked={newMember.assigned_events.includes(event.event_id)}
                            onCheckedChange={() => toggleEventAssignment(event.event_id, true)}
                          />
                          <label htmlFor={`new-${event.event_id}`} className="text-sm cursor-pointer">
                            {event.event_name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Create Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            {members.length} team member{members.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No team members yet</p>
              <p className="text-sm">Click "Add Team Member" to create one</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Assigned Events</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.member_id}>
                    <TableCell className="font-medium">{member.full_name}</TableCell>
                    <TableCell className="text-gray-600">{member.email}</TableCell>
                    <TableCell>
                      {member.role === 'super_admin' ? (
                        <Badge className="bg-purple-100 text-purple-800">
                          <Shield className="h-3 w-3 mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-800">
                          <UserCog className="h-3 w-3 mr-1" />
                          Event Manager
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.role === 'super_admin' ? (
                        <span className="text-gray-500 text-sm">All events</span>
                      ) : member.assigned_events?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {member.assigned_events.slice(0, 2).map((eventId) => (
                            <Badge key={eventId} variant="outline" className="text-xs">
                              {member.assigned_event_names?.[eventId] || eventId.slice(0, 8)}...
                            </Badge>
                          ))}
                          {member.assigned_events.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{member.assigned_events.length - 2} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">None assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {member.last_login ? formatToLocalDateTime(member.last_login) : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingMember(member)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setResetPasswordMember(member)}
                          title="Reset Password"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteMember(member.member_id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update member details and permissions</DialogDescription>
          </DialogHeader>
          {editingMember && (
            <form onSubmit={handleUpdateMember} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={editingMember.full_name}
                  onChange={(e) => setEditingMember({ ...editingMember, full_name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={editingMember.email} disabled className="bg-gray-50" />
                <p className="text-xs text-gray-500">Email cannot be changed</p>
              </div>
              
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={editingMember.role}
                  onValueChange={(v) => setEditingMember({ 
                    ...editingMember, 
                    role: v, 
                    assigned_events: v === 'super_admin' ? [] : editingMember.assigned_events 
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="event_manager">Event Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editingMember.status}
                  onValueChange={(v) => setEditingMember({ ...editingMember, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {editingMember.role === 'event_manager' && (
                <div className="space-y-2">
                  <Label>Assigned Events</Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {events.map((event) => (
                      <div key={event.event_id} className="flex items-center gap-2">
                        <Checkbox
                          id={`edit-${event.event_id}`}
                          checked={(editingMember.assigned_events || []).includes(event.event_id)}
                          onCheckedChange={() => toggleEventAssignment(event.event_id)}
                        />
                        <label htmlFor={`edit-${event.event_id}`} className="text-sm cursor-pointer">
                          {event.event_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditingMember(null)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordMember} onOpenChange={(open) => !open && setResetPasswordMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordMember?.full_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <p className="text-xs text-gray-500">
                Must include uppercase, lowercase, number, and special character
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setResetPasswordMember(null)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Reset Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TeamManagement;
